import { useRef, useState } from 'react';

// components
import Image from 'next/image';
import VisibleLoading from '@/components/utilities/VisibleLoading';

// utilities
import { getImagePath } from '@/utils/images';
import { getDocPath } from '@/utils/docs';

const NewsletterShowcase = ({ eyebrow, title, description, beforeLabel, afterLabel, beforeImage, afterHtml }) => {
  const [mode, setMode] = useState('desktop');
  const [opacity, setOpacity] = useState(1);
  const [iframeHeight, setIframeHeight] = useState(3000);
  const wrapRef = useRef(null);
  const iframeRef = useRef(null);

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const h = iframe.contentDocument.documentElement.scrollHeight;
      if (h > 200) setIframeHeight(h);
    } catch (e) {
      console.log('error', e);
      setIframeHeight(3000);
    }
  };

  const switchMode = (next) => {
    if (next === mode) return;
    setOpacity(0);
    setTimeout(() => {
      setMode(next);
      if (wrapRef.current) wrapRef.current.scrollTop = 0;
      setOpacity(1);
    }, 150);
  };

  const isMobile = mode === 'mobile';

  return (
    <div className="mx-auto my-10 w-[90vw] xl:w-(--container-width)">
      <div className="mb-14 text-center">
        {eyebrow && (
          <VisibleLoading delay={75}>
            {({ ref, className }) => (
              <span
                ref={ref}
                className={`text-brand-500 mb-[14px] inline-block text-sm font-semibold tracking-[0.1em] uppercase ${className || ''}`.trim()}
              >
                {eyebrow}
              </span>
            )}
          </VisibleLoading>
        )}
        {title && (
          <VisibleLoading delay={150}>
            {({ ref, className }) => (
              <h2
                ref={ref}
                className={`text-grey-950 mb-4 text-3xl leading-[1.2] font-light tracking-[-0.5px] lg:text-[40px] ${className || ''}`.trim()}
              >
                {title}
              </h2>
            )}
          </VisibleLoading>
        )}
        {description && (
          <VisibleLoading delay={225}>
            {({ ref, className }) => (
              <p
                ref={ref}
                className={`text-grey-500 mx-auto max-w-[520px] text-lg leading-[1.6] ${className || ''}`.trim()}
              >
                {description}
              </p>
            )}
          </VisibleLoading>
        )}
      </div>

      <div className="grid items-start gap-7 lg:grid-cols-[1fr_1.3fr]">
        <NewsletterCard label={beforeLabel} labelClassName="bg-[#1D1816] text-white" delay={150}>
          <div className="h-[560px] overflow-x-hidden overflow-y-scroll bg-white [scrollbar-color:rgba(0,0,0,0.12)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-[2px] [&::-webkit-scrollbar-thumb]:bg-black/15 [&::-webkit-scrollbar-track]:bg-transparent">
            {beforeImage && (
              <Image
                src={getImagePath(beforeImage)}
                width={beforeImage.width}
                height={beforeImage.height}
                alt={beforeImage.alternativeText || ''}
                sizes="(min-width: 1024px) 500px, 90vw"
                className="block h-auto w-full"
              />
            )}
          </div>
        </NewsletterCard>

        <NewsletterCard
          label={afterLabel}
          labelClassName="bg-brand-400 text-white"
          delay={300}
          barChildren={
            <>
              <div className="flex-1" />
              <div className="border-grey-100 flex rounded-full border bg-white p-[3px]">
                <ToggleButton active={!isMobile} onClick={() => switchMode('desktop')} label="Desktop">
                  <DesktopIcon />
                </ToggleButton>
                <ToggleButton active={isMobile} onClick={() => switchMode('mobile')} label="Mobil">
                  <MobileIcon />
                </ToggleButton>
              </div>
            </>
          }
        >
          <div className={`relative ${isMobile ? 'h-[620px] bg-[#e0e0e0]' : 'h-[560px]'}`}>
            {isMobile && (
              <>
                <div className="pointer-events-none absolute top-5 left-1/2 z-10 h-[580px] w-[389px] -translate-x-1/2 rounded-[32px] border-[7px] border-[#1c1c1e] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.07),0_0_0_1px_rgba(0,0,0,0.3),0_16px_40px_rgba(0,0,0,0.3)] max-[600px]:w-[329px]" />
                <div className="pointer-events-none absolute top-[27px] left-1/2 z-[12] h-[22px] w-[88px] -translate-x-1/2 rounded-b-[14px] bg-[#1c1c1e]" />
              </>
            )}
            <div
              ref={wrapRef}
              style={{ opacity, transition: 'opacity 0.15s' }}
              className={
                isMobile
                  ? 'absolute top-[27px] left-1/2 h-[566px] w-[375px] -translate-x-1/2 overflow-x-hidden overflow-y-scroll rounded-[25px] bg-white [-ms-overflow-style:none] [scrollbar-width:none] max-[600px]:w-[315px] [&::-webkit-scrollbar]:hidden'
                  : 'h-full w-full overflow-x-hidden overflow-y-scroll bg-white [scrollbar-color:rgba(0,0,0,0.12)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-[2px] [&::-webkit-scrollbar-thumb]:bg-black/15 [&::-webkit-scrollbar-track]:bg-transparent'
              }
            >
              {afterHtml && (
                <iframe
                  ref={iframeRef}
                  src={getDocPath(afterHtml)}
                  title="Živý náhled newsletteru"
                  onLoad={handleIframeLoad}
                  style={{
                    width: isMobile ? '375px' : '100%',
                    height: iframeHeight + 'px',
                    display: 'block',
                    border: 'none',
                    overflow: 'hidden'
                  }}
                />
              )}
            </div>
          </div>
        </NewsletterCard>
      </div>
    </div>
  );
};
export default NewsletterShowcase;

const NewsletterCard = ({ label, labelClassName, delay, barChildren, children }) => (
  <VisibleLoading delay={delay}>
    {({ ref, className }) => (
      <div ref={ref} className={className || ''}>
        <div className="overflow-hidden rounded-2xl bg-white shadow-2xl/15 transition-all duration-300 ease-in-out hover:-translate-y-1 hover:shadow-2xl/25">
          <div className="bg-grey-50 flex flex-shrink-0 items-center gap-[10px] p-5">
            <span
              className={`inline-flex items-center rounded-full px-7 py-[14px] text-sm leading-none font-bold tracking-[0.09em] uppercase ${labelClassName}`}
            >
              {label}
            </span>
            {barChildren}
          </div>
          {children}
        </div>
      </div>
    )}
  </VisibleLoading>
);

const ToggleButton = ({ active, onClick, label, children }) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    className={`inline-flex cursor-pointer items-center gap-[5px] rounded-full border-none px-[11px] py-[5px] text-sm leading-none font-medium whitespace-nowrap transition-all duration-[180ms] ${
      active ? 'bg-brand-400 text-white' : 'text-grey-500 hover:text-grey-950 bg-transparent'
    }`}
  >
    {children}
    {label}
  </button>
);

const DesktopIcon = () => (
  <svg width="13" height="10" viewBox="0 0 13 10" fill="none" aria-hidden="true">
    <rect x=".6" y=".6" width="11.8" height="7.3" rx="1.4" stroke="currentColor" strokeWidth="1.2" />
    <path d="M4 9.5h5M6.5 7.9v1.6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const MobileIcon = () => (
  <svg width="8" height="12" viewBox="0 0 8 12" fill="none" aria-hidden="true">
    <rect x=".6" y=".6" width="6.8" height="10.8" rx="1.4" stroke="currentColor" strokeWidth="1.2" />
    <circle cx="4" cy="9.5" r=".75" fill="currentColor" />
  </svg>
);
